import { getLocaleData, setLocaleData } from '@wordpress/i18n';

// On-page language switching (Languages.php, the settings payload's
// `languages`). UI strings: this plugin's text domain in wp.i18n — the same
// instance WordPress filled with the site language's translations — gets
// another language's `wp i18n make-json` file. Catalog texts: the payload
// already carries them per language, see localizeSettings().

const DOMAIN = 'sprintray-roi-calculator';

// Source strings are English.
const SOURCE_PLURAL_FORMS = 'nplurals=2; plural=(n != 1);';

const requests = {};

const fetchTranslations = ( url ) => {
	requests[ url ] ??= window
		.fetch( url )
		.then( ( response ) => {
			if ( ! response.ok ) {
				throw new Error(
					`Loading translations failed (${ response.status })`
				);
			}

			return response.json();
		} )
		.catch( ( error ) => {
			// Let a later click try again.
			delete requests[ url ];
			throw error;
		} );

	return requests[ url ];
};

// Context-prefixed keys ("context\u0004string") translate to their string.
const sourceString = ( key ) => key.split( '\u0004' ).pop();

export const loadLanguage = async ( language ) => {
	const json = language.translationsUrl
		? await fetchTranslations( language.translationsUrl )
		: null;
	const messages = json?.locale_data?.messages ?? {};

	// setLocaleData() merges into the domain's current data, so every
	// string the previous language translated is first set back to its
	// source — else one missing from the new language keeps the old text.
	const data = Object.fromEntries(
		Object.keys( getLocaleData( DOMAIN ) ?? {} )
			.filter( Boolean )
			.map( ( key ) => [ key, [ sourceString( key ) ] ] )
	);

	setLocaleData(
		{
			...data,
			...messages,
			'': {
				'plural-forms': SOURCE_PLURAL_FORMS,
				...messages[ '' ],
				domain: DOMAIN,
				lang: language.wpLocale,
			},
		},
		DOMAIN
	);
};

const withTexts = ( item, texts ) => ( { ...item, ...texts?.[ item.id ] } );

// The settings payload in another language: Languages.php only sends what
// differs from the site's own texts, everything else stays.
export const localizeSettings = ( settings, language ) => {
	if ( ! settings || ! language ) {
		return settings;
	}

	const { texts } = language;

	return {
		...settings,
		// Number/currency formatting follows the language too.
		locale: texts ? language.code : settings.locale,
		pdfLocale: language.wpLocale,
		...( texts && {
			introText: texts.introText ?? settings.introText,
			noticeText: texts.noticeText ?? settings.noticeText,
			partnerLabNote: texts.partnerLabNote ?? settings.partnerLabNote,
			calculationMethod:
				texts.calculationMethod ?? settings.calculationMethod,
			capsuleSizeLabels:
				texts.capsuleSizeLabels ?? settings.capsuleSizeLabels,
			products: ( settings.products ?? [] ).map( ( product ) => ( {
				...withTexts( product, texts.products ),
				materials: ( product.materials ?? [] ).map( ( material ) =>
					withTexts( material, texts.materials )
				),
				indications: ( product.indications ?? [] ).map(
					( indication ) => withTexts( indication, texts.indications )
				),
			} ) ),
		} ),
	};
};
